import React, { useEffect, useState } from 'react';
import { Download, Smartphone, X, CheckCircle2, Sparkles, Monitor } from 'lucide-react';

export const PWAInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [installedSuccess, setInstalledSuccess] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsInstallable(false);
      setInstalledSuccess(true);
      setTimeout(() => setInstalledSuccess(false), 5000);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback hint for iOS / Safari / Manual PWA install
      alert('To install AbilityOS as a PWA on iOS/Safari: Tap the "Share" icon and select "Add to Home Screen".');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      console.log('[PWA] User accepted PWA installation');
      setInstalledSuccess(true);
    }
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  if (dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full p-4 bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white rounded-2xl border border-blue-500/40 shadow-2xl space-y-3 animate-fade-in">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-blue-500/20 border border-blue-400/40 rounded-xl flex items-center justify-center text-amber-400">
            <Smartphone className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-black text-white flex items-center gap-1.5">
              <span>Install AbilityOS PWA App</span>
              <span className="px-1.5 py-0.2 bg-emerald-500/30 text-emerald-200 text-[9px] font-extrabold rounded">
                PWA Ready
              </span>
            </div>
            <div className="text-[11px] text-blue-200">Add to Home Screen / Desktop for offline access</div>
          </div>
        </div>

        <button 
          onClick={() => setDismissed(true)}
          className="text-slate-400 hover:text-white p-1 rounded-lg"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {installedSuccess ? (
        <div className="p-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-xs font-bold rounded-xl text-center flex items-center justify-center gap-1.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>AbilityOS PWA App Installed Successfully!</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={handleInstallClick}
            className="flex-1 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl text-xs shadow-md transition flex items-center justify-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Install PWA App (1-Click)</span>
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-slate-300 font-bold rounded-xl text-xs"
          >
            Later
          </button>
        </div>
      )}
    </div>
  );
};
